/**
 * The age line, and the sign-in identity used by accounts that have no email.
 *
 * The threshold is 14, not 13. COPPA's line is 13, but Quebec's Law 25 requires
 * parental consent below 14, and one number covers both — a 13-year-old in
 * Quebec is otherwise a gap nobody would ever notice.
 *
 * Spec: docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §3, §4.
 */

/** The child band is "this age and under". 13 and under ⇒ child; 14+ ⇒ general. */
export const CHILD_MAX_AGE = 13;

export type AgeBand = 'child' | 'general';

/**
 * Month is 1-12.
 *
 * We take birth year and month only — never the day, and we never store the
 * date itself. Collecting a child's date of birth in order to decide that we
 * should collect nothing else would defeat the entire point; the band is all
 * that is kept.
 *
 * Someone whose 14th birthday falls later this month is still 13, so this
 * rounds toward "child", which is the safe direction to be wrong in.
 *
 * `now` is REQUIRED rather than defaulted: src/config must stay deterministic
 * (tests/unit/purity.test.ts enforces it), and a function that silently reads
 * the wall clock is not. The caller supplies the clock.
 */
export function ageBandFor(birthYear: number, birthMonth: number, now: Date): AgeBand {
  const years = now.getUTCFullYear() - birthYear;
  const months = now.getUTCMonth() + 1 - birthMonth;
  const age = months < 0 ? years - 1 : years;
  return age <= CHILD_MAX_AGE ? 'child' : 'general';
}

/**
 * Accounts with no email still need an identity: Supabase Auth requires an
 * email or a phone number for password sign-in. This address is an identifier
 * and never a mailbox.
 *
 * It lives on a SUBDOMAIN on purpose. hambitower.com carries a catch-all that
 * forwards inbound mail, and these addresses must stay outside it — so
 * users.hambitower.com must have no MX record, making them undeliverable by
 * construction rather than by policy.
 */
export const SYNTHETIC_DOMAIN = 'users.hambitower.com';

/**
 * Derived from the username rather than looked up, so the client can build it
 * for sign-in without an RPC that would map names to user ids.
 *
 * The cost: renaming an account must move auth.users.email with it, or the
 * player can no longer sign in — and a player on this path has no email to
 * recover through. See the rename Edge Function.
 */
export function syntheticAddress(username: string): string {
  return `u_${username.toLowerCase()}@${SYNTHETIC_DOMAIN}`;
}

export function isSyntheticAddress(email: string): boolean {
  return email.toLowerCase().endsWith(`@${SYNTHETIC_DOMAIN}`);
}
